/**
 * OnboardingStepButton - Individual step in onboarding checklist
 * Displays completion state, lock status, and handles click navigation
 * @atomic @idempotent
 */
import { memo } from 'react'
import { useTranslation } from 'react-i18next'
import { LockClosedIcon, CheckIcon } from '@heroicons/react/24/outline'

const OnboardingStepButton = memo(function OnboardingStepButton({
  step,
  isDone,
  isLocked,
  onClick,
  stepsNamespace = 'steps'
}) {
  const { t } = useTranslation()
  const isClickable = !isDone

  return (
    <li>
      <button
        type="button"
        onClick={(e) => {
          e.preventDefault()
          e.stopPropagation()
          onClick(step)
        }}
        onTouchEnd={(e) => { 
          e.preventDefault()
          e.stopPropagation()
          onClick(step)
        }}
        disabled={isDone}
        className={`no-focus-ring w-full flex items-center gap-2.5 text-sm text-left group relative ${
          isClickable 
            ? 'cursor-pointer hover:bg-gray-50 active:bg-gray-100 active:scale-[0.99] -mx-2 px-2 py-1.5 rounded-lg transition-all duration-150' 
            : 'cursor-default opacity-60'
        }`}
        style={{ 
          WebkitTapHighlightColor: 'transparent',
          outline: 'none',
          boxShadow: 'none'
        }}
        title={isClickable ? t(`onboarding.welcome.progress.hints.${step}`) : undefined}
      >
        {isLocked ? (
          <span className="w-5 h-5 rounded-full flex-shrink-0 flex items-center justify-center bg-gray-50 border border-gray-200 pointer-events-none">
            <LockClosedIcon className="h-2.5 w-2.5 text-gray-300" />
          </span>
        ) : (
          <span className={`w-5 h-5 rounded-full flex-shrink-0 flex items-center justify-center transition-all pointer-events-none ${
            isDone 
              ? 'bg-primary text-white' 
              : 'bg-white border-2 border-gray-200 text-gray-300 can-hover:group-hover:border-primary can-hover:group-hover:shadow-sm'
          }`}>
            {isDone ? <CheckIcon className="h-3 w-3" /> : null}
          </span>
        )}
        <span className={`flex-1 text-sm transition-colors pointer-events-none ${
          isDone ? 'text-gray-400 line-through' : isLocked ? 'text-gray-400' : 'text-gray-700 can-hover:group-hover:text-gray-900 font-medium'
        }`}>
          {t(`onboarding.welcome.progress.${stepsNamespace}.${step}`)}
        </span>
        
        {/* Action hint for incomplete steps — always visible on touch devices, hover-reveal on desktop */}
        {isClickable && !isLocked && (
          <span className="text-xs font-medium text-primary can-hover:opacity-0 can-hover:group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap">
            {t('common.clickToStart')} →
          </span>
        )}
        {isClickable && isLocked && (
          <span className="text-xs font-medium text-gray-400 can-hover:opacity-0 can-hover:group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap">
            {t('common.signUp')} →
          </span>
        )}
      </button>
    </li>
  )
})

export default OnboardingStepButton
